import i18next, { t } from "i18next";

/**
 * Resolve the active i18n locale for `toLocaleString`. Falls back to "en"
 * when the i18n stack hasn't initialised yet or sends a value the browser's
 * Intl can't parse — keeps customer deployments in non-DACH locales sane.
 */
const resolveLocale = (): string => {
  try {
    const lang = i18next.language;
    if (typeof lang === "string" && lang.length > 0) return lang;
  } catch {
    // i18next not initialised yet — fall through
  }
  return "en";
};

export const getTimeDifference = (startDate: Date, endDate: Date) => {
  const startStamp = startDate.getTime();
  const endStamp = endDate.getTime();

  let diff = Math.round((endStamp - startStamp) / 1000);

  const days = Math.floor(diff / (24 * 60 * 60));
  diff = diff - days * 24 * 60 * 60;
  const hours = Math.floor(diff / (60 * 60));
  diff = diff - hours * 60 * 60;
  const minutes = Math.floor(diff / 60);
  diff = diff - minutes * 60;
  const min = t("time.unit.minutes");
  const hr = t("time.unit.hours");
  const day = t("time.unit.days");
  if (!days && !hours) return `${minutes} ${min}`;
  if (!days) return `${hours} ${hr}, ${minutes} ${min}`;
  return `${days} ${day}, ${hours} ${hr}, ${minutes} ${min}`;
};

export const formatDateWithSuffix = (date: Date) => {
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
  };
  let formatted: string;
  try {
    formatted = date.toLocaleString(resolveLocale(), opts);
  } catch {
    // Browser/ICU rejected the locale (very rare on full-ICU Chromium,
    // possible in stripped Node test environments). Hard-fallback to "en".
    formatted = date.toLocaleString("en", opts);
  }
  return `${formatted} ${t("time.suffix")}`;
};
